import {createStyles} from '@mantine/core';
import React, {useEffect, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

// Can use this in calculations (ex: to shift HorizontalControls when left sidepanel open)
export const FLOWCHART_SIDE_PANEL_WIDTH = 40;

export const FLOWCHART_SIDEPANEL_Z_INDEX = 10;

interface IconConfig {
  name: BenchlingIconName;
  onClick: () => void;
  tooltip?: string;
  tooltipPlacement?: FloatingPlacement;
}

export interface FlowchartSidebarProps {
  icons: Array<IconConfig>;
  selectedIconIndex: number | null;
  setSelectedIconIndex: (index: number | null) => void;
}

/**
 * A sidebar to toggle between different flowchart-related settings and content.
 *
 * @example
 * function FlowchartSidebarExample() {
 *   const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);
 *   const [isSidepanelOpen, setIsSidepanelOpen] = useState<boolean>(false);

 *   const iconsToDisplay = [
 *     {
 *       name: 'wand-magic-sparkles',
 *       onClick: () => {
 *         setIsSidepanelOpen(true);
 *       },
 *       tooltip: 'Add Nodes',
 *     },
 *   ];
 *
 *   return (
 *     <div>
 *       <FlowchartSidebar
 *         icons={iconsToDisplay}
 *         selectedIconIndex={selectedIconIndex}
 *         setSelectedIconIndex={setSelectedIconIndex}
 *       />
 *       <FlowchartContextPanel
 *         isOpen={isSidepanelOpen}
 *         onClose={() => {
 *           setIsSidepanelOpen(false);
 *           setSelectedIconIndex(null);
 *         }}
 *         sidepanelName="Add Nodes"
 *         sidepanelIcon="wand-magic-sparkles"
 *         panelSide={PanelSide.LEFT}
 *       >
 *         Add Nodes content
 *       </FlowchartContextPanel>
 *     </div>
 *   );
 * }
 */
const useStyles = createStyles((theme, {isSelected}: {isSelected: boolean}) => ({
  sidebarContainer: {
    width: `${FLOWCHART_SIDE_PANEL_WIDTH}px`,
    height: '100%',
    backgroundColor: theme.white,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
  },
  icon: {
    width: '16px',
    height: '16px',
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '6px',
  },
  iconWrapper: {
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    borderRadius: '4px',
    color: isSelected ? theme.colors.blue[7] : theme.colors.gray[6],
    backgroundColor: isSelected ? theme.colors.blue[1] : 'transparent',
    '&:hover': {
      backgroundColor: isSelected ? theme.colors.blue[1] : theme.colors.gray[1],
    },
    '&:active': {
      backgroundColor: isSelected ? theme.colors.blue[3] : theme.colors.gray[2],
    },
  },
}));

function SidebarIcon({
  iconConfig,
  isSelected,
  onClick,
}: {
  iconConfig: IconConfig;
  isSelected: boolean;
  onClick: () => void;
}): JSX.Element {
  const {classes} = useStyles({isSelected});
  return (
    <Tooltip placement={iconConfig.tooltipPlacement ?? FloatingPlacement.TOP} text={iconConfig.tooltip}>
      <div className={classes.iconContainer}>
        <div
          className={classes.iconWrapper}
          onClick={onClick}
          {...getTestProps({
            component: 'FlowchartSidebar',
            element: 'interactiveIcon',
            key: iconConfig.name,
          })}
        >
          <BenchlingIcon icon={iconConfig.name} className={classes.icon} />
        </div>
      </div>
    </Tooltip>
  );
}

function FlowchartSidebar({
  icons,
  selectedIconIndex,
  setSelectedIconIndex,
}: FlowchartSidebarProps): JSX.Element {
  const {classes} = useStyles({isSelected: false});
  const prevSelectedIconIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      selectedIconIndex != null &&
      selectedIconIndex !== prevSelectedIconIndexRef.current &&
      icons[selectedIconIndex]
    ) {
      icons[selectedIconIndex].onClick();
    }
    prevSelectedIconIndexRef.current = selectedIconIndex;
  }, [selectedIconIndex, icons]);

  return (
    <div
      className={classes.sidebarContainer}
      {...getTestProps({component: 'FlowchartSidebar', element: 'root'})}
    >
      {icons.map((iconConfig, index) => (
        <SidebarIcon
          key={index}
          iconConfig={iconConfig}
          isSelected={selectedIconIndex === index}
          onClick={() => {
            if (selectedIconIndex === index) {
              setSelectedIconIndex(null);
            } else {
              setSelectedIconIndex(index);
            }
          }}
        />
      ))}
    </div>
  );
}

export default FlowchartSidebar;
